// Storefront coupon banner — the one seller coupon currently live and Display-on for this store.
//
// Deliberately shaped on ShippingBar (same file, same folder): a slim, full-width, info-only
// announcement strip. Same type scale (11.5px/400), same vertical rhythm (py-[9px]), same bottom
// hairline, so it reads as a sibling of the strips already under the store header rather than
// something bolted on.
//
// ONE treatment on both surfaces — same copy AND same styling, per the decision that the store
// home and the PDP show the same thing.
//
// The ground is cream (#FFF8E7), deliberately NOT ShippingBar's lavender (#F4F5FF): an earlier
// revision matched ShippingBar exactly and the two merged into a single double-height lavender
// block, since the coupon bar sits directly above it. Cream also separates the promotional strip
// from the informational one, which is the distinction a shopper actually needs.
//
// ⚠️ On the PDP this sits above the win-back bar's surface-peach (#FEEFEB). Both are pale and
// warm, so that adjacency is the one worth eyeballing.
//
// No dismiss. When the live coupon has a festival page (couponLandingPageSlug), the whole bar links
// to it; otherwise (custom coupon, festival without a page) it is display only, as before. Renders
// null when there is no live coupon — hidden entirely, never an empty bar.

import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
// The validity phrase lives in its own module so it can be unit-tested with an injected clock —
// see Utils/couponValidity.test.js and the timezone note in that file.
import { formatValidity } from "../../Utils/couponValidity";
import { isEmpty } from "lodash";

// One class string for the bar and its loading placeholder, so the reserved space is exactly the
// bar's height (same padding, font size, line height and border).
const BAR_CLASS =
  "flex items-center justify-center gap-2 px-[14px] py-[9px] text-[11.5px] font-[400] leading-[1.45] text-center text-ink-900 hover:text-ink-900 bg-[#FFF8E7] border-b border-[#E8C97A]/[0.35]";

export default function CouponBar() {
  // state.storeDetails is /store/open/details, fetched ONCE app-wide in App.js and already
  // populated by the time either surface renders — so this costs no request on the store home
  // and no request on the PDP, which is the whole reason the coupon rides on this payload rather
  // than an endpoint of its own.
  //
  // The three fields are flat, not nested, and are always present or absent together: the server
  // writes all three or clears all three. `code` alone is still the guard, because it is the one
  // field the bar cannot render without.
  const storeDetails = useSelector((s) => s.storeDetails);
  const code = storeDetails?.couponCode;
  const percent = storeDetails?.couponPercent;

  // FIXED RESERVED HEIGHT while store_details is still loading (App.js fetches it; it can land
  // after the page has painted on a slow connection). Without this the bar appeared late and
  // pushed the whole page down. The placeholder is the bar's own shell with one invisible line,
  // so the real bar drops into the same space. A store with no live coupon collapses it once the
  // details arrive -- a one-line shift, only for those stores, only on a slow first load.
  if (isEmpty(storeDetails)) {
    return (
      <div aria-hidden="true" className={BAR_CLASS}>
        <span className="invisible">&nbsp;</span>
      </div>
    );
  }

  if (!code || !percent) return null;

  // "today only" / "till tomorrow" / "till 10 Nov". Day + short month, no year: the window is at
  // most a few months out in practice, and a year would push the line toward wrapping on a 360px
  // phone for nothing the shopper needs. Null valid_till means the coupon never expires, and
  // formatValidity returns null for it — the bar then shows code + percent only.
  const validity = formatValidity(storeDetails?.couponValidTill);
  // Set by the server only when this live coupon drives a festival page.
  const pageSlug = storeDetails?.couponLandingPageSlug;
  const Bar = pageSlug ? Link : "div";
  const barProps = pageSlug
    ? { to: `/${pageSlug}` }
    : { role: "status", "aria-live": "off" };

  return (
    <Bar
      {...barProps}
      /* No whitespace-nowrap: on a narrow phone this WRAPS to a second line rather than
         overflowing or truncating. The wrap changes the bar's height, which PromoStack's
         ResizeObserver picks up, so --wb-offer-h stays correct. (The win-back bar next to it IS
         nowrap — it is one short line by design.) */
      className={BAR_CLASS}
    >
      <span>
        <span aria-hidden="true">🎟️</span>{" "}
        <strong className="font-[500] tracking-[0.02em]">{code}</strong>
        {" — "}
        <span className="font-[500]">{percent}% off</span>
        {validity && (
          <>
            {" · "}
            <span className="text-ink-700">{validity}</span>
          </>
        )}
        {pageSlug && <span aria-hidden="true">{" →"}</span>}
      </span>
    </Bar>
  );
}
